import { invariant } from "@phosphor/utils";
import { base } from "w3c-keyname";

const isMac = typeof navigator !== "undefined" && /Mac/.test(navigator.platform);

type Mod<T extends string> = "" | `${T}-`;

type DefaultSingleKeyCombos =
  | "Tab"
  | "Escape"
  | "Enter"
  | "ArrowUp"
  | "ArrowDown"
  | "ArrowLeft"
  | "ArrowRight"
  | "Backspace"
  | "A"
  | "B"
  | "C"
  | "D"
  | "E"
  | "F"
  | "G"
  | "H"
  | "I"
  | "J"
  | "K"
  | "L"
  | "M"
  | "N"
  | "O"
  | "P"
  | "Q"
  | "R"
  | "S"
  | "T"
  | "U"
  | "V"
  | "W"
  | "X"
  | "Y"
  | "Z"
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9";

export type DefaultKeyCombo =
  | DefaultSingleKeyCombos
  | `${"Shift" | "AltOpt"}-${DefaultSingleKeyCombos}`
  | `${Mod<"ShiftOpt" | "Shift" | "AltOpt">}CtrlCmd-${string}`;

export const normalizedKeyCombo = (keyCombo: DefaultKeyCombo, keyboard: KeyboardKind) => {
  if (keyboard === KeyboardKind.macOS) {
    return keyKeyName(
      keyboard,
      keyCombo
        .replace(/CtrlCmd/g, "Cmd")
        .replace(/ShiftOpt/g, "Opt")
        .replace(/AltOpt/g, "Opt"),
    );
  } else {
    return keyKeyName(
      keyboard,
      keyCombo
        .replace(/CtrlCmd/g, "Ctrl")
        .replace(/ShiftOpt/g, "Shift")
        .replace(/AltOpt/g, "Alt"),
    );
  }
};

export enum KeyboardKind {
  PC,
  macOS,
}

export const ENV_KEYBOARD_KIND = isMac ? KeyboardKind.macOS : KeyboardKind.PC;

export type KeyboardCombo = {
  normalized: string;
  keyboard: KeyboardKind;
};

const RE_ASCII = /^[a-z]$/u;
const normalizeSingleKey = (key: string): string => {
  if (key.length === 1 && RE_ASCII.test(key)) return key.toUpperCase();
  return key;
};

function keyKeyName(keyboard: KeyboardKind, name: string): KeyboardCombo {
  const parts = name.split(/-(?!$)/);
  const lastIdx = parts.length - 1;
  let result = parts[lastIdx]!;
  result = normalizeSingleKey(result);
  if (result == "Space") result = " ";
  if (keyboard === KeyboardKind.macOS) {
    let opt = false,
      shift = false,
      cmd = false;
    for (let i = 0; i < lastIdx; i++) {
      const mod = parts[i]!;
      if (/^(Ctrl)?Cmd$/i.test(mod)) cmd = true;
      else if (/^(Shift|Alt)?Opt$/i.test(mod)) opt = true;
      else if (/^s(hift)?$/i.test(mod)) shift = true;
      else throw new Error("Unrecognized modifier name for macOS: " + mod);
    }
    if (shift) result = "Shift-" + result;
    if (opt) result = "Opt-" + result;
    if (cmd) result = "Cmd-" + result;
  } else {
    invariant(keyboard === KeyboardKind.PC, "expected keyboard to be PC", { keyboard });
    let alt = false,
      meta = false,
      ctrl = false,
      shift = false;
    for (let i = 0; i < lastIdx; i++) {
      const mod = parts[i]!;
      if (/^(Ctrl)?Cmd$/i.test(mod)) ctrl = true;
      else if (/^Shift(Opt)?$/i.test(mod)) shift = true;
      else if (/^Alt(Opt)?$/i.test(mod)) alt = true;
      else if (/^(c|ctrl|control)$/i.test(mod)) ctrl = true;
      else if (/^s(hift)?$/i.test(mod)) shift = true;
      else if (/^(meta|super)$/i.test(mod)) meta = true;
      else throw new Error("Unrecognized modifier name for PC: " + JSON.stringify(mod));
    }
    if (shift) result = "Shift-" + result;
    if (meta) result = "Meta-" + result;
    if (alt) result = "Alt-" + result;
    if (ctrl) result = "Ctrl-" + result;
  }
  return { normalized: result, keyboard };
}

const MODIFIER_KEYS = new Set(["Shift", "Meta", "Ctrl", "Alt", "Control"]);
const CODE_TO_KEY = new Map(
  Object.entries({
    Digit0: "0",
    Digit1: "1",
    Digit2: "2",
    Digit3: "3",
    Digit4: "4",
    Digit5: "5",
    Digit6: "6",
    Digit7: "7",
    Digit8: "8",
    Digit9: "9",
    KeyA: "A",
    KeyB: "B",
    KeyC: "C",
    KeyD: "D",
    KeyE: "E",
    KeyF: "F",
    KeyG: "G",
    KeyH: "H",
    KeyI: "I",
    KeyJ: "J",
    KeyK: "K",
    KeyL: "L",
    KeyM: "M",
    KeyN: "N",
    KeyO: "O",
    KeyP: "P",
    KeyQ: "Q",
    KeyR: "R",
    KeyS: "S",
    KeyT: "T",
    KeyU: "U",
    KeyV: "V",
    KeyW: "W",
    KeyX: "X",
    KeyY: "Y",
    KeyZ: "Z",
  }),
);

const RE_COMMON_KEY = /^[a-zA-Z0-9;'"\[\]\\,\.\/]$/;

export function addModifiersToKeyCombo(
  keyboard: KeyboardKind,
  event: KeyboardEvent,
  omitShift: boolean = false,
): KeyboardCombo[] {
  const candidates: KeyboardCombo[] = [];
  const isMac = keyboard === KeyboardKind.macOS;

  const withModifiers = (key: string): KeyboardCombo | null => {
    key = normalizeSingleKey(key);
    if (MODIFIER_KEYS.has(key)) return null;

    if (!omitShift && event.shiftKey) {
      key = "Shift-" + key;
    }
    if (event.altKey) key = (isMac ? "Opt-" : "Alt-") + key;
    if (!isMac && event.ctrlKey) key = "Ctrl-" + key;
    if (event.metaKey) key = (isMac ? "Cmd-" : "Meta-") + key;
    return keyKeyName(keyboard, key);
  };

  const getBaseKey = (): string | undefined => {
    let baseKey = CODE_TO_KEY.get(event.code);

    if (!baseKey && event.code.startsWith("Key") && RE_ASCII.test(event.code.slice(-1))) {
      baseKey = normalizeSingleKey(event.code.slice(-1));
    }

    if (!baseKey && event.code.endsWith("BracketLeft")) {
      baseKey = "[";
    } else if (!baseKey && event.code.endsWith("BracketRight")) {
      baseKey = "]";
    }

    if (!baseKey && base[event.keyCode] && base[event.keyCode] !== event.key) {
      baseKey = base[event.keyCode]!.toUpperCase();
    }

    return baseKey;
  };

  if (event.key.charCodeAt(0) > 127 || (event.code.startsWith("Key") && event.key !== getBaseKey()?.toLowerCase())) {
    const directCombo = withModifiers(event.key);
    if (directCombo) candidates.push(directCombo);
  }

  if (RE_COMMON_KEY.test(event.key)) {
    const directCombo = withModifiers(event.key);
    if (directCombo && !candidates.some((c) => c.normalized === directCombo.normalized)) {
      candidates.push(directCombo);
    }
  }

  const baseKey = getBaseKey();
  if (baseKey) {
    const baseCombo = withModifiers(baseKey);
    if (baseCombo && !candidates.some((c) => c.normalized === baseCombo.normalized)) {
      candidates.push(baseCombo);
    }
  }

  if (candidates.length === 0) {
    const fallbackCombo = withModifiers(event.key);
    if (fallbackCombo) candidates.push(fallbackCombo);
  }

  return candidates;
}
